<template>
    <!--
        Table view of agencies, dynamic columns + per-cell rendering by slug.
        Cell content is dispatched off `col.slug` so we keep the parent's
        existing column-edit feature working (current_columns is mutable
        from the Column_Modal). Unknown slugs fall through to a plain text
        rendering of `item[slug]`.
    -->
    <div :style="wrapStyle">
        <table :style="tableStyle">
            <colgroup>
                <col :style="{ width: '38px' }" />
                <col
                    v-for="c in visibleColumns"
                    :key="'col-' + c.id"
                    :style="{ width: columnWidth(c) }"
                />
                <col :style="{ width: '40px' }" />
            </colgroup>
            <thead>
                <tr>
                    <th :style="headCellStyle({ sticky: 'left' })">
                        <input
                            type="checkbox"
                            :checked="allSelected"
                            :style="{ margin: '0 auto', display: 'block' }"
                            @change="$emit('toggle-all', $event.target.checked)"
                        />
                    </th>
                    <th
                        v-for="c in visibleColumns"
                        :key="'th-' + c.id"
                        :style="headCellStyle({ align: columnAlign(c) })"
                        @click="$emit('sort', c.slug)"
                    >
                        <span :style="headLabelStyle(c)">
                            {{ c.label || c.slug }}
                            <Icon
                                v-if="lastSort === c.slug"
                                :name="descending ? 'arrowDown' : 'arrowUp'"
                                :size="10"
                                :style="{ color: 'var(--tag-red)' }"
                            />
                        </span>
                    </th>
                    <th :style="headCellStyle({ align: 'right' })" />
                </tr>
            </thead>
            <tbody>
                <tr
                    v-for="(item, idx) in items"
                    :key="'row-' + (rowId(item) || idx)"
                    :style="rowStyle(idx, item)"
                    @click="$emit('row-click', item)"
                >
                    <td :style="bodyCellStyle({ sticky: 'left' })">
                        <input
                            type="checkbox"
                            :checked="isSelected(item)"
                            :style="{ margin: '0 auto', display: 'block' }"
                            @click.stop
                            @change="$emit('toggle-row', { id: rowId(item), checked: $event.target.checked })"
                        />
                    </td>

                    <!-- Per-column cell renderer -->
                    <td
                        v-for="c in visibleColumns"
                        :key="'td-' + (rowId(item) || idx) + '-' + c.id"
                        :style="bodyCellStyle({ align: columnAlign(c) })"
                    >
                        <!-- Name column: mark + name + #id -->
                        <template v-if="c.slug === 'name'">
                            <span :style="nameWrapStyle">
                                <AgencyMark :name="nameOf(item)" />
                                <span :style="nameStackStyle">
                                    <a
                                        v-if="nameLinkOf(item)"
                                        :href="nameLinkOf(item)"
                                        :style="nameLinkStyle"
                                        @click.stop
                                    >{{ nameOf(item) }}</a>
                                    <span v-else :style="nameLinkStyle">{{ nameOf(item) }}</span>
                                    <span v-if="rowId(item)" class="tag-mono" :style="idStyle">#{{ paddedId(item) }}</span>
                                </span>
                            </span>
                        </template>

                        <!-- Status: agency pill -->
                        <template v-else-if="c.slug === 'status'">
                            <AgencyPill :status="String(item[c.slug] || '')" />
                        </template>

                        <!-- Source: dot + label -->
                        <template v-else-if="c.slug === 'source'">
                            <span :style="sourceCellStyle">
                                <span :style="sourceDotStyle" />
                                {{ item[c.slug] || '—' }}
                            </span>
                        </template>

                        <!-- Service: compact badge -->
                        <template v-else-if="c.slug === 'service'">
                            <span :style="serviceBadgeStyle">{{ item[c.slug] || '—' }}</span>
                        </template>

                        <!-- Money columns -->
                        <template v-else-if="moneySlugs.includes(c.slug)">
                            <Money :value="item[c.slug]" />
                        </template>

                        <!-- Number columns -->
                        <template v-else-if="numberSlugs.includes(c.slug)">
                            <span
                                class="tag-mono tag-tnum"
                                :style="numStyle(item[c.slug])"
                            >{{ formatNumber(item[c.slug]) }}</span>
                        </template>

                        <!-- Date columns -->
                        <template v-else-if="dateSlugs.includes(c.slug)">
                            <span class="tag-mono tag-tnum" :style="dateStyle">
                                {{ item[c.slug] || '—' }}
                            </span>
                        </template>

                        <!-- Fallback: plain text -->
                        <template v-else>
                            <span :style="plainCellStyle">{{ item[c.slug] != null ? item[c.slug] : '—' }}</span>
                        </template>
                    </td>

                    <!-- Inline row actions -->
                    <td :style="bodyCellStyle({ align: 'right' })">
                        <span :style="actionsCellStyle">
                            <a
                                v-if="editHrefOf(item)"
                                :href="editHrefOf(item)"
                                class="btn btn--icon btn--xs btn--ghost"
                                :style="actionBtnStyle"
                                @click.stop
                            >
                                <Icon name="edit" />
                            </a>
                            <button
                                type="button"
                                class="btn btn--icon btn--xs btn--ghost"
                                :style="actionBtnStyle"
                                @click.stop="$emit('delete', rowId(item))"
                            >
                                <Icon name="trash" />
                            </button>
                        </span>
                    </td>
                </tr>

                <tr v-if="!items.length">
                    <td :colspan="visibleColumns.length + 2" :style="emptyCellStyle">
                        No agencies match your filters.
                    </td>
                </tr>
            </tbody>
        </table>
    </div>
</template>

<script>
import Icon from "./Icon.vue";
import AgencyMark from "./AgencyMark.vue";
import AgencyPill from "./AgencyPill.vue";
import Money from "./Money.vue";

// Slugs that should render as money cells / numeric / date — keeps the
// existing backend column shape working without per-column config.
const MONEY_SLUGS = [
    "current_invoice", "current_commissions",
    "ytd_sales_amount", "ytd_commissions",
    "total_invoice", "total_commissions",
];
const NUMBER_SLUGS = ["active_deals", "ytd_deals", "total_deals"];
const DATE_SLUGS = ["created_at", "updated_at"];
const RIGHT_ALIGN_SLUGS = [...MONEY_SLUGS, ...NUMBER_SLUGS];

export default {
    name: "AgenciesTable",
    components: { Icon, AgencyMark, AgencyPill, Money },
    props: {
        items: { type: Array, default: () => [] },
        columns: { type: Array, default: () => [] },
        selectedIds: { type: Array, default: () => [] },
        lastSort: { type: String, default: "" },
        descending: { type: Boolean, default: false },
    },
    computed: {
        moneySlugs() { return MONEY_SLUGS; },
        numberSlugs() { return NUMBER_SLUGS; },
        dateSlugs() { return DATE_SLUGS; },
        visibleColumns() {
            return (this.columns || [])
                .filter((c) => c.status !== "hidden")
                .sort((a, b) => (a.position || 0) - (b.position || 0));
        },
        allSelected() {
            return this.items.length > 0 && this.items.every((i) => this.isSelected(i));
        },
        wrapStyle() {
            return { position: "relative", overflow: "auto", flex: "1 1 auto" };
        },
        tableStyle() {
            return {
                width: "100%",
                borderCollapse: "separate",
                borderSpacing: 0,
                fontSize: "var(--table-fz)",
            };
        },
        nameWrapStyle() {
            return {
                display: "inline-flex",
                alignItems: "center",
                gap: "10px",
                minWidth: 0,
            };
        },
        nameStackStyle() {
            return { display: "flex", flexDirection: "column", minWidth: 0 };
        },
        nameLinkStyle() {
            return {
                fontWeight: 500,
                color: "var(--text)",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                textDecoration: "none",
            };
        },
        idStyle() {
            return { fontSize: "10.5px", color: "var(--text-faint)" };
        },
        sourceCellStyle() {
            return {
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                color: "var(--text-muted)",
            };
        },
        sourceDotStyle() {
            return {
                width: "6px",
                height: "6px",
                borderRadius: "50%",
                background: "color-mix(in oklch, var(--text-muted) 50%, transparent)",
            };
        },
        serviceBadgeStyle() {
            return {
                fontSize: "11.5px",
                padding: "2px 7px",
                background: "var(--surface-2)",
                border: "1px solid var(--border-faint)",
                borderRadius: "4px",
                color: "var(--text-muted)",
            };
        },
        dateStyle() {
            return { color: "var(--text-muted)" };
        },
        plainCellStyle() {
            return { color: "var(--text-muted)" };
        },
        actionsCellStyle() {
            return {
                display: "inline-flex",
                gap: "2px",
                justifyContent: "flex-end",
            };
        },
        actionBtnStyle() {
            return {
                color: "var(--text-faint)",
                textDecoration: "none",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
            };
        },
        emptyCellStyle() {
            return {
                padding: "32px 16px",
                textAlign: "center",
                color: "var(--text-faint)",
                fontSize: "13px",
            };
        },
    },
    methods: {
        columnWidth(c) {
            if (c.width) return typeof c.width === "number" ? `${c.width}px` : c.width;
            // Reasonable defaults per slug — these match the designer's
            // AGENCY_COLUMNS widths so the table matches at first paint.
            const map = {
                name: "280px",
                status: "130px",
                source: "110px",
                service: "130px",
                active_deals: "120px",
                ytd_deals: "100px",
                ytd_sales_amount: "130px",
                ytd_commissions: "120px",
                created_at: "110px",
            };
            return map[c.slug] || "120px";
        },
        columnAlign(c) {
            return RIGHT_ALIGN_SLUGS.includes(c.slug) ? "right" : "left";
        },
        headCellStyle({ align = "left", sticky = null } = {}) {
            return {
                padding: "10px 12px",
                background: "var(--surface-2)",
                borderBottom: "1px solid var(--border)",
                fontSize: "var(--header-fz)",
                fontWeight: 600,
                color: "var(--text-muted)",
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                textAlign: align,
                position: sticky ? "sticky" : undefined,
                left: sticky === "left" ? 0 : undefined,
                zIndex: sticky ? 2 : undefined,
                cursor: "pointer",
                userSelect: "none",
            };
        },
        bodyCellStyle({ align = "left", sticky = null } = {}) {
            return {
                padding: "var(--cell-py, 8px) 12px",
                borderBottom: "1px solid var(--border-faint)",
                textAlign: align,
                verticalAlign: "middle",
                position: sticky ? "sticky" : undefined,
                left: sticky === "left" ? 0 : undefined,
                background: sticky ? "inherit" : undefined,
                zIndex: sticky ? 1 : undefined,
            };
        },
        headLabelStyle(c) {
            return {
                display: "inline-flex",
                alignItems: "center",
                gap: "5px",
                color: this.lastSort === c.slug ? "var(--text)" : undefined,
            };
        },
        rowStyle(idx, item) {
            const selected = this.isSelected(item);
            const bg = selected
                ? "color-mix(in oklch, var(--tag-red-soft) 70%, var(--surface))"
                : idx % 2 === 1
                  ? "var(--surface-2)"
                  : "var(--surface)";
            return {
                background: bg,
                cursor: "pointer",
            };
        },
        rowId(item) {
            // The backend's reformatDataByColumns serialises id either at
            // the top level or inside the `name` object — try both.
            if (item.id != null) return item.id;
            if (item.name && item.name.id != null) return item.name.id;
            return null;
        },
        paddedId(item) {
            const id = this.rowId(item);
            return id == null ? "" : String(id).padStart(4, "0");
        },
        nameOf(item) {
            const v = item.name;
            if (v && typeof v === "object") return v.name || "";
            return v || "";
        },
        nameLinkOf(item) {
            const v = item.name;
            if (v && typeof v === "object" && v.link) return v.link;
            const id = this.rowId(item);
            return id ? `/agencies/${id}/edit` : null;
        },
        editHrefOf(item) {
            const id = this.rowId(item);
            return id ? `/agencies/${id}/edit` : null;
        },
        isSelected(item) {
            const id = this.rowId(item);
            return id != null && this.selectedIds.includes(id);
        },
        formatNumber(value) {
            if (value == null || value === "") return "—";
            const n = Number(value);
            if (!Number.isFinite(n)) return "—";
            if (n === 0) return "—";
            return n.toLocaleString("en-US");
        },
        numStyle(value) {
            const n = Number(value);
            const isEmpty = !Number.isFinite(n) || n === 0;
            return {
                fontWeight: isEmpty ? 400 : 500,
                color: isEmpty ? "var(--text-faint)" : "var(--text)",
            };
        },
    },
};
</script>
